import { createContext, useContext, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ease } from "../motion";

export const C = {
  cyan: "#00F0FF",
  blue: "#3B82F6",
  green: "#10B981",
  red: "#F43F5E",
  amber: "#F59E0B",
  line: "rgba(148,163,184,0.35)",
  dim: "#64748B",
  text: "#E2E8F0",
  body: "#0B0F17",
};

export const Svg = ({ children }) => (
  <svg viewBox="0 0 600 400" className="h-full w-full" role="img">
    <defs>
      <filter id="aglow" x="-200%" y="-200%" width="500%" height="500%">
        <feGaussianBlur stdDeviation="2.5" result="b" />
        <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
      <linearGradient id="silver" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#E8ECF1" />
        <stop offset="1" stopColor="#AEB6C2" />
      </linearGradient>
    </defs>
    {children}
  </svg>
);

export const Draw = ({ d, delay = 0, color = C.line, w = 1.4, dur = 1 }) => (
  <motion.path d={d} fill="none" stroke={color} strokeWidth={w} strokeLinecap="round" initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }} transition={{ duration: dur, delay, ease }} />
);

export const Dashed = ({ d, delay = 0, color = C.line, w = 1.2, flow }) => (
  <motion.path d={d} fill="none" stroke={color} strokeWidth={w} strokeDasharray="4 7" className={flow ? "flow" : ""} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay }} />
);

export const Packet = ({ d, dur = 2, begin = 0, color = C.cyan, r = 2.8 }) => (
  <circle r={r} fill={color} filter="url(#aglow)" opacity="0">
    <animateMotion dur={`${dur}s`} begin={`${begin}s`} repeatCount="indefinite" path={d} />
    <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.85;1" dur={`${dur}s`} begin={`${begin}s`} repeatCount="indefinite" />
  </circle>
);

export const Pop = ({ children, delay = 0 }) => (
  <motion.g initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay, ease }}>
    {children}
  </motion.g>
);

export const Label = ({ x, y, children, color = C.dim, size = 9, anchor = "middle", weight, family = "JetBrains Mono, monospace", spacing = "0.12em", ...rest }) => (
  <text {...rest} x={x} y={y} textAnchor={anchor} fill={color} fontSize={size} fontWeight={weight} fontFamily={family} letterSpacing={spacing}>
    {children}
  </text>
);

export const Unit = ({ x, y, w = 170, h = 22, ports = 8, sfp = 2, screen = true }) => {
  const start = x + (screen ? 44 : 14);
  const pw = Math.min(10, (w - (start - x) - 14 - sfp * 14) / ports - 2);
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="2.5" fill="url(#silver)" stroke="#8A94A3" strokeWidth="0.6" />
      {screen && <rect x={x + 8} y={y + 4} width="28" height={h - 8} rx="1.5" fill="#0B1220" />}
      {screen && <circle cx={x + 22} cy={y + h / 2} r="2.2" fill={C.blue} className="blink" />}
      {Array.from({ length: ports }).map((_, i) => (
        <g key={i}>
          <rect x={start + i * (pw + 2)} y={y + 6} width={pw} height={h - 12} rx="1" fill="#1F2937" />
          <rect x={start + i * (pw + 2) + 1} y={y + 5} width="2" height="1.6" fill={C.green} className="blink" style={{ animationDelay: `${(i * 0.37) % 1.4}s` }} />
        </g>
      ))}
      {Array.from({ length: sfp }).map((_, i) => (
        <rect key={i} x={x + w - 12 - (sfp - i) * 14 + 12} y={y + 6} width="11" height={h - 12} rx="1" fill="#374151" />
      ))}
    </g>
  );
};

export const APDisc = ({ x, y, r = 15 }) => (
  <g>
    <circle cx={x} cy={y} r={r} fill="#F1F5F9" stroke="#CBD5E1" strokeWidth="0.8" />
    <circle cx={x} cy={y} r={r * 0.45} fill="none" stroke={C.blue} strokeWidth="1.6" filter="url(#aglow)" className="breathe" />
  </g>
);

export const PauseContext = createContext({ current: false });

export const useTick = (fn, ms) => {
  const paused = useContext(PauseContext);
  const cb = useRef(fn);
  cb.current = fn;
  useEffect(() => {
    const id = setInterval(() => !paused.current && cb.current(), ms);
    return () => clearInterval(id);
  }, [ms, paused]);
};

export const useCycle = (say, lines, ms = 3400) => {
  const i = useRef(0);
  useEffect(() => say(lines[0]), [say, lines]);
  useTick(() => say(lines[(i.current = (i.current + 1) % lines.length)]), ms);
};

export const Icon = ({ as: I, x, y, s = 18, color = C.text }) => <I x={x - s / 2} y={y - s / 2} width={s} height={s} color={color} strokeWidth={1.5} />;
